import React, { useEffect, useMemo } from 'react';
import './SubmissionView.css';
import './IdePage.css';
import { FINAL_PROBLEM_ID, PROBLEMS } from './problems';
import IDE from './IDE';

const TraceTable = ({ traceVars = [], variableNames = [] }) => {
  const maxCols = Math.max(
    variableNames.length,
    ...(traceVars.map((r) => (Array.isArray(r) ? r.length : 0)))
  );
  const headers = Array.from({ length: maxCols }, (_, i) => variableNames[i] ?? `var${i + 1}`);
  const rows = traceVars.map((row) => Array.from({ length: maxCols }, (_, i) => row?.[i] ?? { value: '' }));

  const getCell = (cell) => {
    if (typeof cell === 'object' && cell && 'value' in cell) return String(cell.value ?? '');
    return String(cell ?? '');
  };

  return (
    <table className="trace-table">
      <thead>
        <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>
            {headers.map((_, ci) => (
              <td key={ci}><code>{getCell(row[ci])}</code></td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const safeParse = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const loadSubmissionSnapshot = () => {
  const problemId = FINAL_PROBLEM_ID;
  const steps = safeParse(`rt_${problemId}_steps`, null) ?? safeParse('rt_steps', []);

  const defaultVars = PROBLEMS[problemId]?.defaultVariables || [];
  const variableNamesCandidate =
    safeParse(`rt_${problemId}_varnames`, null) ??
    safeParse('rt_varnames', null) ??
    defaultVars;
  const variableNames = Array.isArray(variableNamesCandidate) && variableNamesCandidate.length > 0
    ? variableNamesCandidate
    : defaultVars;

  const programInput = (() => {
    try {
      const raw = localStorage.getItem('rt_program_input');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'string') return parsed;
      }
    } catch {}
    return PROBLEMS[problemId]?.defaultInput || '';
  })();

  let code = '';
  try {
    const raw = localStorage.getItem('rt_ide_code');
    if (typeof raw === 'string') code = raw;
  } catch {
    code = '';
  }
  if (!code || code === 'null' || code === 'undefined') {
    code = PROBLEMS[problemId]?.starterCode || '';
  }

  return {
    problemId,
    problemTitle: PROBLEMS[problemId]?.title || 'Your submission',
    problemDescription: PROBLEMS[problemId]?.description || '',
    defaultInput: PROBLEMS[problemId]?.defaultInput || '',
    steps: Array.isArray(steps) ? steps : [],
    variableNames,
    programInput,
    code,
  };
};

const StepsList = ({ steps, variableNames }) => {
  if (!steps?.length) {
    return (
      <div className="submission-empty-card">
        <p>No saved steps were found.</p>
        <p className="submission-muted">Finish the task to see your trace here.</p>
      </div>
    );
  }

  const renderStep = (step, idx) => {
    const indentPx = Math.max(0, Number(step?.indent ?? 0)) * 50;
    const rawRows = Array.isArray(step?.traceVars) && step.traceVars.length > 0
      ? step.traceVars
      : [variableNames.map(() => ({ value: 'uninitialized' }))];
    const widest = Math.max(
      variableNames.length,
      ...(rawRows.map((r) => (Array.isArray(r) ? r.length : 0)))
    );
    const names = Array.from({ length: widest }, (_, i) => variableNames[i] ?? `var${i + 1}`);
    const rows = rawRows.map((row) => {
      const arr = Array.isArray(row) ? row : [];
      return Array.from({ length: widest }, (_, i) => {
        const cell = arr[i];
        if (typeof cell === 'object' && cell && 'value' in cell) {
          return { value: String(cell.value ?? '') };
        }
        return { value: String(cell ?? '') };
      });
    });

    return (
      <div key={step?.id ?? idx} className="draggable-wrapper">
        <div className="step-row">
          <div className="indent-spacer" style={{ width: `${indentPx}px` }}>
            {indentPx > 0 && (
              <div className="substep-indicator">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 4V14C10 14.5304 10.2107 15.0391 10.5858 15.4142C10.9609 15.7893 11.4696 16 12 16H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M15 11L20 16L15 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            )}
          </div>
          <div
            className={`step-container ${step?.isCorrect === true ? 'correct' : ''} ${step?.isCorrect === false ? 'incorrect' : ''}`}
          >
            <div className="description-wrapper">
              <textarea
                className="description-box"
                value={step?.description ?? ''}
                placeholder="Describe your step in natural language..."
                readOnly
              />
              <div className="trace-area">
                <div className="trace-vars-container">
                  <div className="iteration-rows-container">
                    {rows.map((row, rowIndex) => (
                      <div key={rowIndex} className="iteration-row-wrapper">
                        <div className="remove-iteration-wrapper" />
                        <div className="iteration-row">
                          {row.map((variable, varIndex) => (
                            <div key={varIndex} className="trace-var-tag">
                              <span className="tooltip-wrap" data-tooltip="Variable name">
                                <input
                                  type="text"
                                  className="trace-var-name-input"
                                  value={names[varIndex]}
                                  readOnly
                                  spellCheck={false}
                                />
                              </span>
                              <span className="tooltip-wrap" data-tooltip="Variable value">
                                <input
                                  type="text"
                                  className="trace-var-input"
                                  value={variable.value}
                                  readOnly
                                  spellCheck={false}
                                  size={Math.max(15, variable.value.length)}
                                />
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="add-iteration-wrapper" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="submission-steps-list">
      {steps.map((step, idx) => renderStep(step, idx))}
    </div>
  );
};

const StepsTableList = ({ steps, variableNames }) => {
  if (!steps?.length) {
    return (
      <div className="no-steps-message">
        No steps yet. Go back and submit your solution to see them here.
      </div>
    );
  }

  return (
    <div className="steps-pane-content">
      {steps.map((step, i) => (
        <div
          key={i}
          className="step-display-container"
          style={{ marginLeft: `${Number(step?.indent ?? 0) * 50}px` }}
        >
          <div className="step-description-display">
            <strong>Step {i + 1}:</strong> {step.description || ''}
          </div>

          {Array.isArray(step.traceVars) && step.traceVars.length > 0 && (
            <div className="step-trace-display">
              <TraceTable traceVars={step.traceVars} variableNames={variableNames} />
            </div>
          )}

          {step.simulation ? (
            <div className="step-simulation-display">
              <pre>{step.simulation}</pre>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export function SubmissionTaskPage() {
  const snapshot = useMemo(() => loadSubmissionSnapshot(), []);
  const problem = PROBLEMS[snapshot.problemId] || {};

  useEffect(() => {
    try {
      localStorage.setItem('rt_last_route', '/submission/task');
      localStorage.setItem('rt_flow_stage', 'done');
    } catch {}
  }, []);

  return (
    <div className="submission-view-page app-container submission-readonly">
      <header>
        <h1>RunThrough</h1>
        <div className="problem-title">
          <h3>{problem.title}</h3>
          <p>{snapshot.problemDescription}</p>
        </div>
      </header>

      <main>
        <div className="main-program-input-container">
          <label className="program-input-label">Input</label>
          <textarea
            className="program-input-box"
            value={snapshot.programInput}
            readOnly
            rows={1}
            spellCheck={false}
          />
        </div>

        <StepsList steps={snapshot.steps} variableNames={snapshot.variableNames} />
      </main>
    </div>
  );
}

export function SubmissionCodePage() {
  const snapshot = useMemo(() => loadSubmissionSnapshot(), []);
  const problem = PROBLEMS[snapshot.problemId] || {};

  useEffect(() => {
    try {
      localStorage.setItem('rt_last_route', '/submission/code');
      localStorage.setItem('rt_flow_stage', 'done');
    } catch {}
  }, []);

  return (
    <div className="submission-view-page submission-readonly submission-code-only">
      <header>
        <h1>RunThrough</h1>
        <div className="problem-title">
          <h3>{problem.title || 'Your submission'}</h3>
          <p className="submission-muted">Read-only view of your submitted IDE code.</p>
        </div>
      </header>

      <div className="ide-only-shell">
        <IDE
          value={snapshot.code || '# No code submission found.'}
          onChange={() => {}}
          onSubmit={() => {}}
          showInlineSubmit={false}
          readOnly
        />
      </div>
    </div>
  );
}
